import { createCustomErrorMessage } from "../hooks/errorMessage";
import { DocumentType } from "../models/DocumentType";
import { isIEFPUser } from "../authentication/authenticationHelper";

export const EaAccreditationFormCustomValidator = {
  validateEmailsNifsAndNipc: (formikValues, setError, intl, errorMessages) =>
    validateEmailsNifsAndNipc(formikValues, setError, intl, errorMessages),
  validateGETListSize: (formikValues, setError, intl, errorMessages) =>
    validateGETListSize(formikValues, setError, intl, errorMessages),
  validateTGPListSize: (formikValues, setError, intl, errorMessages) =>
    validateTGPListSize(formikValues, setError, intl, errorMessages),
  validateTgpMaxCount: (formikValues, intl, errorMessages) =>
    validateTgpMaxCount(formikValues, intl, errorMessages),
  validateNewTgps: (formikValues, intl, setFieldError, errorMessages) =>
    validateNewTgps(formikValues, intl, setFieldError, errorMessages),
  validateTgpMappings: (formikValues, intl, errorMessages) =>
    validateTgpMappings(formikValues, intl, errorMessages),
  validateGETAndDistricts: (
    formikValues,
    setError,
    intl,
    errorMessages,
    setFieldError,
    districtList,
    skipOnRenewal = false
  ) =>
    validateGETAndDistricts(
      formikValues,
      setError,
      intl,
      errorMessages,
      setFieldError,
      districtList,
      skipOnRenewal
    ),
  validateSsAndAtAuthorizations: (
    formikValues,
    entityAccreditationDocuments,
    setError,
    intl,
    errorMessages
  ) =>
    validateSsAndAtAuthorizations(
      formikValues,
      entityAccreditationDocuments,
      setError,
      intl,
      errorMessages
    ),
  validateRenewalDocuments: (
    formikValues,
    renewalDocuments,
    setError,
    intl,
    errorMessages
  ) =>
    validateRenewalDocuments(
      formikValues,
      renewalDocuments,
      setError,
      intl,
      errorMessages
    ),
};

function validateEmailsNifsAndNipc(
  formikValues,
  setError,
  intl,
  errorMessages
) {
  let emails = [formikValues.entity.email];
  let nifs = [formikValues.entity.nif];
  formikValues.getList.forEach((get) => emails.push(get.email));
  formikValues.tgpList.forEach((tgp) => nifs.push(tgp.hre.nif));

  const emailOccurrences = emails.reduce((acc, email) => {
    return acc[email] ? ++acc[email] : (acc[email] = 1), acc;
  }, {});

  const nifOccurrences = nifs.reduce((acc, nif) => {
    return acc[nif] ? ++acc[nif] : (acc[nif] = 1), acc;
  }, {});

  if (
    Object.values(emailOccurrences).filter(
      (numberOfOccurrences) => numberOfOccurrences > 1
    ).length > 0 ||
    Object.values(nifOccurrences).filter(
      (numberOfOccurrences) => numberOfOccurrences > 1
    ).length > 0
  ) {
    errorMessages.push(
      intl.formatMessage({
        id: "errors.eaAccreditationForm.inconsistentNifsAndEmails",
      })
    );
    return false;
  }

  return true;
}

function validateGETListSize(formikValues, setError, intl, errorMessages) {
  if (formikValues.getList.length === 0) {
    errorMessages.push(
      intl.formatMessage({
        id: "errors.eaAccreditationForm.getList",
      })
    );
    return false;
  }
  return true;
}

function validateTGPListSize(formikValues, setError, intl, errorMessages) {
  if (formikValues.tgpList.length === 0) {
    errorMessages.push(
      intl.formatMessage({
        id: "errors.eaAccreditationForm.tgpList",
      })
    );
    return false;
  }
  return true;
}

function validateSsAndAtAuthorizations(
  formikValues,
  entityAccreditationDocuments,
  setError,
  intl,
  errorMessages
) {
  if (!formikValues.entity.atDeclarationAuthorized) {
    if (
      entityAccreditationDocuments.filter(
        (d) => d.documentType === "AT_DECLARATION"
      )[0].submited === false
    ) {
      errorMessages.push(
        intl.formatMessage({
          id: "errors.eaAccreditationForm.ssAndAt",
        })
      );

      return false;
    }
  }

  if (!formikValues.entity.ssDeclarationAuthorized) {
    if (
      entityAccreditationDocuments.filter(
        (d) => d.documentType === "SS_DECLARATION"
      )[0].submited === false
    ) {
      errorMessages.push(
        intl.formatMessage({
          id: "errors.eaAccreditationForm.ssAndAt",
        })
      );
      return false;
    }
  }

  return true;
}

function districtToDelegationMap(districtList) {
  let map = {};
  districtList.forEach((d) => (map[d.code] = d.regionalDelegationCode));
  return map;
}

function validateGETAndDistricts(
  formikValues,
  setError,
  intl,
  errorMessages,
  setFieldError,
  districtList,
  skipOnRenewal = false
) {
  if (skipOnRenewal === true) {
    return true;
  }

  let d2RDMap = districtToDelegationMap(districtList);
  let getRDSet = new Set(
    formikValues.getList.map((get) => get.regionalDelegationCode)
  );
  let disRDSet = new Set(
    formikValues.interventionDistrictCodes.map((d) => d2RDMap[d])
  );

  let getError = false;
  let disError = false;

  getRDSet.forEach((get) => {
    if (!disRDSet.has(get)) {
      setFieldError(
        get,
        intl.formatMessage({
          id: "errors.eaAccreditationForm.GETWithoutDistrict",
        })
      );
      getError = true;
    }
  });

  formikValues.interventionDistrictCodes.forEach((dist) => {
    if (!getRDSet.has(d2RDMap[dist])) {
      setFieldError(
        `interventionDistrictCodes.${dist}`,
        intl.formatMessage({
          id: "errors.eaAccreditationForm.DistrictWithoutGet",
        })
      );
      disError = true;
    }
  });

  if (getError) {
    errorMessages.push(
      intl.formatMessage({
        id: "errors.eaAccreditationForm.GETWithoutDistrict",
      })
    );
  }

  if (disError) {
    errorMessages.push(
      intl.formatMessage({
        id: "errors.eaAccreditationForm.DistrictWithoutGet",
      })
    );
  }

  if (getError || disError) return false;

  return true;
}

function validateRenewalDocuments(
  formikValues,
  renewalDocuments,
  setError,
  intl,
  errorMessages
) {
  const isIefp = isIEFPUser ? isIEFPUser() : false;

  const hasSubmitted = (docType) => {
    const d = (renewalDocuments || []).find(
      (doc) => doc.documentType === docType
    );
    return !!(d && d.submited === true);
  };

  if (!hasSubmitted(DocumentType.RENEWAL_INVITATION_LETTER)) {
    errorMessages.push(
      intl.formatMessage({
        id: "ea.accreditation.renewal.error.uploadDocuments",
      })
    );
    return false;
  }

  if (!hasSubmitted(DocumentType.RENEWAL_ENTITY_RESPONSE_TO_INVITATION)) {
    errorMessages.push(
      intl.formatMessage({
        id: "ea.accreditation.renewal.error.uploadDocuments",
      })
    );
    return false;
  }

  if (isIefp) {
    if (
      !hasSubmitted(DocumentType.RENEWAL_DIRECTIVE_COUNCIL_SERVICE_INFORMATION)
    ) {
      errorMessages.push(
        intl.formatMessage({
          id: "ea.accreditation.renewal.error.uploadDocuments",
        })
      );
      return false;
    }
  }

  if (!hasSubmitted(DocumentType.RENEWAL_NOTIFICATION_LETTER_AND_PROTOCOL)) {
    errorMessages.push(
      intl.formatMessage({
        id: "ea.accreditation.renewal.error.uploadDocuments",
      })
    );
    return false;
  }

  if (!hasSubmitted(DocumentType.RENEWAL_SIGNED_PROTOCOL)) {
    errorMessages.push(
      intl.formatMessage({
        id: "ea.accreditation.renewal.error.uploadDocuments",
      })
    );
    return false;
  }

  return true;
}

function validateTgpMaxCount(formikValues, intl, errorMessages) {
  const activeCount = (formikValues.tgpList || []).filter(
    (t) => !t.deactivated
  ).length;
  if (activeCount > 5) {
    errorMessages.push(
      intl.formatMessage({ id: "errors.eaAccreditationForm.tgpMax" })
    );
    return false;
  }
  return true;
}

function validateNewTgps(formikValues, intl, setFieldError, errorMessages) {
  let ok = true;
  (formikValues.tgpList || []).forEach((t, i) => {
    if (!t.isNew) return;

    const name = (t.name || "").trim();
    if (!name) {
      setFieldError(
        `tgpList[${i}].name`,
        intl.formatMessage({ id: "errors.fieldRequiredText" })
      );
      ok = false;
    }

    if (!t.qualificationLevelCode || t.qualificationLevelCode === "-1") {
      setFieldError(
        `tgpList[${i}].qualificationLevelCode`,
        intl.formatMessage({ id: "errors.fieldRequiredText" })
      );
      ok = false;
    }

    const nif = String(t?.hre?.nif || "");
    if (!/^\d{9}$/.test(nif)) {
      setFieldError(
        `tgpList[${i}].hre.nif`,
        intl.formatMessage({ id: "errors.invalid.nif" })
      );
      ok = false;
    }

    if (!t?.hre?.bondType || t.hre.bondType === "-1") {
      setFieldError(
        `tgpList[${i}].hre.bondType`,
        intl.formatMessage({ id: "errors.fieldRequiredText" })
      );
      ok = false;
    }

    const intsOk = [
      "projectCreationCount",
      "analysisCount",
      "consultingCount",
      "expandSkillsCount",
      "approvedProjectFollowUpCount",
    ].every((k) => {
      const v = String(t?.hre?.[k] ?? "0");
      return /^\d{1,2}$/.test(v);
    });
    if (!intsOk) {
      errorMessages.push(
        intl.formatMessage({ id: "errors.eaAccreditationForm.tgpYearsInvalid" })
      );
      ok = false;
    }
  });

  if (!ok) {
    errorMessages.push(
      intl.formatMessage({ id: "errors.eaAccreditationForm.tgpRequired" })
    );
  }
  return ok;
}

function validateTgpMappings(formikValues, intl, errorMessages) {
  let ok = true;
  (formikValues.tgpList || []).forEach((t) => {
    if (!t.isNew) return;
    if (!Array.isArray(t.hrMapList) || t.hrMapList.length === 0) {
      ok = false;
    }
  });
  if (!ok) {
    errorMessages.push(
      intl.formatMessage({
        id: "errors.eaAccreditationForm.tgpMappingRequired",
      })
    );
  }
  return ok;
}
